/**
 * Copyright (c) 2010-2022 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanLoad, Route, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { AppPageRoute } from '../../state/appConfig/app-config.model';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { concatMapTo, filter, map, switchMapTo, take, tap } from 'rxjs/operators';
import { Navigate } from '@ngxs/router-plugin';
import { PageState } from '../../state/page/page.state';
import { AuthenticatedGuard } from './authenticated.guard';

@Injectable()
export class DisabledRouteGuard implements CanActivate, CanLoad {
    @Select(PageState.loaded) loaded$: Observable<boolean>;
    @Select(PageState.enabledPages) enabledPages$: Observable<AppPageRoute[]>;

    constructor(private logger: LoggerService, private authenticatedGuard: AuthenticatedGuard, private store: Store) {}

    canLoad(route: Route): Observable<boolean> | Promise<boolean> | boolean {
        return this.authenticatedGuard.canLoad(route).pipe(
            filter((b) => b),
            concatMapTo(this.loaded$),
            filter((b) => b),
            concatMapTo(this.enabledPages$),
            map((enabledPages) => enabledPages.findIndex((p) => p === route.path) !== -1),
            take(1),
            tap((result) => {
                if (!result) {
                    setTimeout(
                        () => this.store.dispatch(new Navigate(['/'], null, { queryParamsHandling: 'preserve' })),
                        0
                    );
                }
            })
        );
    }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> | Promise<boolean> | boolean {
        const splitUrl = state.url.split('?');
        const path = splitUrl[0];

        return this.authenticatedGuard.canActivate(route, state).pipe(
            filter((b) => b),
            concatMapTo(this.loaded$),
            filter((b) => b),
            switchMapTo(this.enabledPages$),
            map((enabledPages) => enabledPages.findIndex((p) => `/${p}` === path) !== -1),
            take(1),
            tap((canActivate) => {
                if (!canActivate) {
                    this.store.dispatch(new Navigate(['/'], null, { queryParamsHandling: 'preserve' }));
                }
            })
        );
    }
}
